import { getSolicitacaoDash } from "@/http/getSolicitacaoDash";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select"
import { UserNav } from "../userNav";
import { CardIndoAdmin } from "../cardIndoAdmin";
import { CalendarCheck2, CircleCheck, Info, LoaderCircle, Timer } from "lucide-react";
import { TableAdmin } from "../tableAdmin";
import { setores } from "@/constants/setores";
import { InputFilterCPF } from "../inputFilterCPF";
import { InputFilterServico } from "../inputFilterServico";
import Empty from "../empty";
import { Badge } from "../ui/badge";
import { parseAsInteger, useQueryState } from "nuqs";
import { NavLinks } from "../navLinks";
import { FilterSwitch } from "../monitoring/FilterSwitch";
import { useRole } from "@/hooks/use-role";
import FilterPage from "../filterPage";


export function ViewDash({ handleDetalhe }) {
  const [setorFilter, setSetorFilter] = useState('');
  const [filter, setFilter] = useQueryState('filter');
  const [cpf, setCpf] = useQueryState('cpf', { defaultValue: "" });
  const [servico, setServico] = useQueryState('servico', { defaultValue: "" });
  const [atendente, setAtendente] = useQueryState('atendente');
  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1))

  const { isAdmin } = useRole();


  function setFilterSetor(value) {
    setPage(1)
    setSetorFilter(value)
    localStorage.setItem("secretaria_setor", value);
  }

  function getFilterSetor() {
    const secretaria_setor = localStorage.getItem("secretaria_setor");
    if (secretaria_setor) {
      setSetorFilter(secretaria_setor)
    }
  }

  useEffect(() => {
    getFilterSetor()
  }, []);

  useEffect(() => {
    if (!isAdmin && servico) {
      setServico("");
    }
  }, [servico]);

  const { data, isLoading } = useQuery({
    queryKey: ['getSolicitacaoDash', setorFilter, filter, cpf, servico, atendente, page],
    queryFn: () => getSolicitacaoDash({ setorFilter, filter, cpf, servico, atendente, page }),
    retry: true
  })

  return (
    <div>
      <header className="h-[70px] border-b border-border bg-zinc-50 flex flex-row justify-between items-center px-6">

        <NavLinks />

        <div className="flex flex-row items-center gap-5">
          <div className="flex gap-2">
            <Select value={setorFilter} id="empresa" onValueChange={(value) => setFilterSetor(value || "")}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Selecione seu setor..." />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Filtrar setor</SelectLabel>
                  <SelectItem>Todos setores</SelectItem>
                  {setores.options.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.label}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <UserNav />
        </div>
      </header>
      <div className="py-8 mx-auto max-w-[1100px]">
        <div className="flex items-center gap-2">
          <p className="text-2xl font-bold tracking-tight">Bem-vindo(a)</p>
          <div className="motion-preset-shake motion-duration-1500">
            <img src="/waving.png" alt="waving" className="size-7" />
          </div>
        </div>
        <p className="text-muted-foreground">Gerencie todas as solicitações.</p>

        <div className="mt-4 grid grid-cols-4 gap-4">
          <CardIndoAdmin loading={isLoading} selected={!filter} label="Pendentes" onClick={() => { setPage(1); setFilter(null) }} value={data?.countPendentes || "0"} bgIconColor="bg-[#ffdebf]" icon={<Info className="size-4 text-[#f38b2b]" />} />
          <CardIndoAdmin loading={isLoading} selected={filter === "Finalizados"} onClick={() => { setPage(1); setFilter("Finalizados") }} label="Finalizadas" value={data?.countFinalizados || "0"} bgIconColor="bg-[#d6f9d0]" icon={<CircleCheck className="size-4 text-[#4fb83e]" />} />
          <CardIndoAdmin loading={isLoading} selected={filter === "FinalizadosHoje"} onClick={() => { setPage(1); setFilter("FinalizadosHoje") }} label="Finalizadas Hoje" value={data?.countFinalizadosHoje || "0"} bgIconColor="bg-[#d6f9d0]" icon={<CalendarCheck2 className="size-4 text-[#4fb83e]" />} />
          <CardIndoAdmin loading={isLoading} selected={filter === "tempoMedio"} onClick={() => { setPage(1); setFilter("tempoMedio") }} label="Tempo Médio de Resposta" value={data?.tempoMedio ? `${Math.floor(parseFloat(data.tempoMedio))} ${data.tempoMedio.split(" ")[1]}` : "0 minutos"} bgIconColor="bg-[#bfc6ff]" icon={<Timer className="size-4 text-[#2b42f3]" />} />
        </div>

        <div className="grid grid-cols-3 mt-4 gap-4 bg-white border border-border rounded-md p-4 items-center">
          <div className="flex items-center gap-3 w-full">
            <p className="text-muted-foreground">Filtros</p>
            {atendente ?
              <Badge className='bg-amber-500 text-[#fff]'><Info />Você está visualizando suas solicitações.</Badge> :
              <Badge variant="secondary"><Info />Você está visualizando <strong>todas as solicitações.</strong></Badge>}
          </div>
          <div className="col-span-2 flex items-center gap-2 w-full">
            {!isAdmin && <div className="flex-1" />}
            <FilterSwitch label="Meus atendimentos" isChecked={Boolean(atendente)} setIsChecked={(checked) => { setPage(1); setAtendente(checked || null) }} />
            <InputFilterCPF filter={cpf} setFilter={(value) => { setPage(1); setCpf(value); }} />
            {isAdmin && <InputFilterServico servico={servico} setFilter={(value) => { setPage(1); setServico(value); }} setor={setorFilter} />}
          </div>
        </div>

        <div className="motion-translate-x-in-[0%] motion-translate-y-in-[-2%] motion-opacity-in-[50%] mt-4 p-4 pb-2 bg-white border border-border rounded-md">
          {isLoading ? <div className='flex h-[300px] justify-center items-center'>
            <LoaderCircle className="animate-spin text-red-500 size-8" />
          </div> : <TableAdmin handleDetalhe={handleDetalhe} data={data?.solicitacoes} />}
          {data && data.solicitacoes.length === 0 && <div className='flex flex-col gap-2 items-center p-8'>
            <Empty />
            <p className='text-[#b2b2b2] text-[12px]'>Nenhum registro encontrado.</p>
          </div>}
        </div>

        {!isLoading && data?.totalPages > 1 && <div className="mt-4 p-4 bg-white border border-border rounded-md">
          <FilterPage totalPages={data?.totalPages} />
        </div>}

      </div>
    </div>
  )
}